import { useState, useEffect } from 'react';
import { ContentImageField } from '@/components/admin/content-image-field';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

export interface QuoteItem {
  id?: string;
  quote: string;
  author: string;
  imageUrl: string;
}

interface QuoteEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteItem: QuoteItem | null;
  onSave: (updatedItem: QuoteItem) => void;
}

export function QuoteEditDialog({
  open,
  onOpenChange,
  quoteItem,
  onSave,
}: QuoteEditDialogProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<QuoteItem>({
    quote: '',
    author: '',
    imageUrl: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when quote item changes or when creating new quote
  useEffect(() => {
    if (quoteItem) {
      setFormData(quoteItem);
    } else {
      // Default values for new quote
      setFormData({
        quote: 'Elke dag is een nieuwe kans om te groeien',
        author: 'Hoofd naar Hart',
        imageUrl:
          'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=1200',
      });
    }
  }, [quoteItem]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // In a future implementation, this will save to Supabase
      // For now, just call the onSave callback with the updated item
      onSave(formData);
      onOpenChange(false);

      toast({
        title: quoteItem ? 'Quote bijgewerkt' : 'Nieuwe quote toegevoegd',
        description: 'De quote is succesvol opgeslagen.',
      });
    } catch (error) {
      console.error('Fout bij opslaan quote:', error);
      toast({
        title: 'Fout',
        description: 'Er is een fout opgetreden bij het opslaan van de quote.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {quoteItem ? 'Quote Bewerken' : 'Nieuwe Quote Toevoegen'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="quote">Quote Tekst</Label>
            <Textarea
              id="quote"
              name="quote"
              value={formData.quote}
              onChange={handleChange}
              rows={3}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="author">Auteur</Label>
            <Input
              id="author"
              name="author"
              value={formData.author}
              onChange={handleChange}
              required
            />
          </div>

          <ContentImageField
            value={formData.imageUrl}
            onChange={(url) =>
              setFormData((prev) => ({ ...prev, imageUrl: url }))
            }
            label="Afbeelding"
            required
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-green-600 text-green-600 hover:bg-green-50"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSaving
                ? 'Opslaan...'
                : quoteItem
                  ? 'Wijzigingen Opslaan'
                  : 'Quote Toevoegen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
